import { ChangeDetectionStrategy, Component, inject, Injectable, OnDestroy } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Observable, Subject, Subscription } from "rxjs";

import { LgTranslateService, useTranslationNamespace } from "@logex/framework/lg-localization";
import {
    getDialogFactoryBase,
    IDialogComponent,
    LgConfirmationDialog,
    LgDialogFactory,
    LgDialogRef
} from "@logex/framework/ui-core";

import { IFlagsDefinition, MIXED_FLAGS_STATUS } from "../flags-menu/types";
import { SaveFlagsObject } from "./types";

@Component({
    selector: "mod-flags-selector",
    templateUrl: "./flags-selector-dialog.component.html",
    styleUrls: ["./flags-selector-dialog.component.scss"],
    viewProviders: [useTranslationNamespace("APP._FlagSelector")],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FlagsSelectorDialogComponent
    implements IDialogComponent<FlagsSelectorDialogComponent>, OnDestroy
{
    private _original: SaveFlagsObject;
    private _formEventsSubscription: Subscription | null;

    protected _lgTranslate = inject(LgTranslateService);
    protected _dialogRef = inject(LgDialogRef<FlagsSelectorDialogComponent>);
    protected _confirm = inject(LgConfirmationDialog);
    protected _formBuilder = inject(FormBuilder);

    _dialogClass = "lg-dialog lg-dialog--3col";
    _title = this._lgTranslate.translate(".FlagStatus");
    _form: FormGroup;
    _definition: IFlagsDefinition;
    _save: Subject<SaveFlagsObject> = new Subject<SaveFlagsObject>();

    ngOnDestroy(): void {
        if (this._formEventsSubscription != null) {
            this._formEventsSubscription.unsubscribe();
        }
    }

    public get _isSubmitDisabled(): boolean {
        return this._form.invalid || !this._form.dirty;
    }

    open(value: SaveFlagsObject, definition: IFlagsDefinition): Observable<SaveFlagsObject> {
        this._definition = definition;
        this._original = Object.assign({}, value);
        this._form = this._formBuilder.group({
            flagId: value.flag_id,
            comment:
                value.flag_comment === MIXED_FLAGS_STATUS
                    ? this._lgTranslate.translate(".FlagCommentMixedMsg")
                    : value.flag_comment
        });

        if (
            value.flag_id === 0 ||
            value.flag_id === MIXED_FLAGS_STATUS ||
            value.flag_comment === MIXED_FLAGS_STATUS
        ) {
            this._form.controls["comment"].disable();
        }

        this._formEventsSubscription = this._form.get("flagId")?.valueChanges.subscribe(x => {
            if (x === 0 || x === MIXED_FLAGS_STATUS) {
                this._form.controls["comment"].setValue("");
                this._form.controls["comment"].disable();
            } else {
                if (value.flag_comment === MIXED_FLAGS_STATUS)
                    this._form.controls["comment"].setValue("");
                this._form.controls["comment"].enable();
            }
        });
        return this._save.asObservable();
    }

    get _isMixedState() {
        return (
            this._original !== undefined &&
            (this._original.flag_id === MIXED_FLAGS_STATUS ||
                this._original.flag_comment === MIXED_FLAGS_STATUS)
        );
    }

    async _onSubmit(): Promise<void> {
        if (this._form.valid) {
            if (this._isMixedState) {
                const result = await this._confirm.confirm(
                    "",
                    this._lgTranslate.translate(".ComfirmDiscardChanges"),
                    {
                        button1: this._lgTranslate.translate(".Overwrite"),
                        button2: this._lgTranslate.translate(".Discard")
                    }
                );
                if (result === 1) {
                    this._save.next({
                        flag_id: this._form.value.flagId,
                        flag_comment: this._form.value.comment
                    });
                    this._close();
                }
            } else {
                this._save.next({
                    flag_id: this._form.value.flagId,
                    flag_comment: this._form.value.comment
                });
                this._close();
            }
        }
    }

    _close(): void {
        this._save.complete();
        this._dialogRef.close();
    }

    _cancel(): void {
        this._close();
    }
}

@Injectable()
export class FlagsSelectorDialog extends getDialogFactoryBase(
    FlagsSelectorDialogComponent,
    "open"
) {
    constructor() {
        super(inject(LgDialogFactory));
    }
}
